import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Download,
  Copy,
  Server,
  CheckCircle,
  Terminal,
  FileText,
  Settings,
  HardDrive,
  Package,
  FileCode,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Monitor,
  Clock,
  Shield,
  Cpu,
  FolderArchive,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Agents = () => {
  const { token, getAuthHeader } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState("veeam");
  const [deployMethod, setDeployMethod] = useState("exe");
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [configForm, setConfigForm] = useState({
    serverUrl: API_URL,
    serverId: "",
    apiKey: "",
    frequencyMinutes: 15,
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copie dans le presse-papier");
  };

  const downloadExePackage = async () => {
    if (!configForm.serverId) {
      toast.error("Veuillez renseigner le Server ID");
      return;
    }
    try {
      const headers = getAuthHeader();
      const res = await fetch(`${API_URL}/api/agent/download/${configForm.serverId}`, { headers });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Erreur de telechargement");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `MySentinelAgent_${selectedAgent}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Package agent telecharge");
    } catch (err) {
      toast.error("Erreur de telechargement");
    }
  };

  const downloadScript = () => {
    const script = selectedAgent === "veeam" ? generateVeeamScript() : generateIperiusScript();
    const filename = selectedAgent === "veeam" ? "MySentinelAgent-Veeam.ps1" : "MySentinelAgent-Iperius.ps1";
    const blob = new Blob([script], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Script telecharge");
  };

  const generateVeeamScript = () => {
    return `# MySentinel Agent - Veeam Backup v3.0 (Multi-VM)
# Voir la documentation complete dans le backoffice
param(
    [string]$ServerUrl = "${configForm.serverUrl}",
    [string]$ServerId = "${configForm.serverId}",
    [string]$ApiKey = "${configForm.apiKey}",
    [int]$FrequencyMinutes = ${configForm.frequencyMinutes}
)
$ConfigFile = "$env:ProgramData\\MySentinel\\config.json"
$LogFile = "$env:ProgramData\\MySentinel\\agent.log"
# Script complet disponible dans le package .exe (recommande)
# Ou telechargez via le bouton "Telecharger le package .exe"
Write-Host "Utilisez de preference le package .exe pour un deploiement simplifie."
`;
  };

  const generateIperiusScript = () => {
    return `# MySentinel Agent - Iperius Backup v2.0
param(
    [string]$ServerUrl = "${configForm.serverUrl}",
    [string]$ServerId = "${configForm.serverId}",
    [string]$ApiKey = "${configForm.apiKey}",
    [int]$FrequencyMinutes = ${configForm.frequencyMinutes}
)
$ConfigFile = "$env:ProgramData\\MySentinel\\config.json"
$LogFile = "$env:ProgramData\\MySentinel\\agent.log"
Write-Host "Utilisez de preference le package .exe pour un deploiement simplifie."
`;
  };

  const generateConfigJson = () => {
    return JSON.stringify(
      {
        server_url: configForm.serverUrl,
        server_id: configForm.serverId,
        api_key: configForm.apiKey,
        agent_type: selectedAgent,
        frequency_minutes: configForm.frequencyMinutes,
      },
      null,
      2
    );
  };

  const agentInfo = {
    veeam: {
      name: "Veeam Backup",
      description: "Agent pour Veeam Backup & Replication. Supporte les jobs multi-VM.",
      features: ["Cmdlets Veeam natifs", "Multi-VM par job", "Analyse logs Windows"],
    },
    iperius: {
      name: "Iperius Backup",
      description: "Agent pour Iperius Backup (toutes versions).",
      features: ["Logs XML", "Logs texte", "Verification service"],
    },
  };

  const docSections = [
    {
      id: "overview",
      title: "Vue d'ensemble",
      icon: BookOpen,
      content: (
        <div className="space-y-3 text-sm text-slate-300">
          <p>
            L'agent ServeurOnline est un programme leger qui s'execute sur vos serveurs Windows. Il collecte automatiquement les resultats de sauvegarde (Veeam ou Iperius) et les envoie au serveur ServeurOnline.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <Cpu className="text-indigo-400 mb-2" size={18} />
              <p className="text-white text-xs font-medium">Leger</p>
              <p className="text-[11px] text-slate-500">Aucune installation requise, un seul fichier .exe</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <Shield className="text-emerald-400 mb-2" size={18} />
              <p className="text-white text-xs font-medium">Securise</p>
              <p className="text-[11px] text-slate-500">Communication HTTPS, cle API unique par serveur</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <Monitor className="text-amber-400 mb-2" size={18} />
              <p className="text-white text-xs font-medium">Multi-VM</p>
              <p className="text-[11px] text-slate-500">Remonte les resultats de chaque VM dans un job Veeam</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "prerequisites",
      title: "Prerequis",
      icon: Settings,
      content: (
        <div className="space-y-2 text-sm text-slate-300">
          <p className="font-medium text-white">Pour la methode .exe (recommandee) :</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 ml-2">
            <li>Windows Server 2012 R2 ou superieur</li>
            <li>Python 3.8+ installe sur la machine de compilation</li>
            <li>Acces reseau au serveur ServeurOnline (HTTPS)</li>
          </ul>
          <p className="font-medium text-white mt-3">Pour la methode PowerShell :</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 ml-2">
            <li>PowerShell 5.0+ (inclus dans Windows Server)</li>
            <li>Droits administrateur (pour les cmdlets Veeam)</li>
          </ul>
        </div>
      ),
    },
    {
      id: "deploy-exe",
      title: "Deploiement .exe (recommande)",
      icon: Package,
      content: (
        <div className="space-y-3 text-sm text-slate-300">
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">1</span>
              <div>
                <p className="font-medium text-white">Creer le serveur dans ServeurOnline</p>
                <p className="text-xs text-slate-500">Clients &gt; Ajouter un serveur &gt; Copiez le Server ID et l'API Key</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">2</span>
              <div>
                <p className="font-medium text-white">Telecharger le package</p>
                <p className="text-xs text-slate-500">Remplissez le formulaire de configuration et cliquez sur "Telecharger le package .exe"</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">3</span>
              <div>
                <p className="font-medium text-white">Compiler sur une machine Windows</p>
                <p className="text-xs text-slate-500">Dezippez le package et lancez <code className="bg-slate-800 px-1 rounded text-emerald-400">build.bat</code> (necessite Python)</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">4</span>
              <div>
                <p className="font-medium text-white">Deployer sur le serveur cible</p>
                <p className="text-xs text-slate-500">
                  Copiez <code className="bg-slate-800 px-1 rounded text-emerald-400">dist/MySentinelAgent.exe</code> et{" "}
                  <code className="bg-slate-800 px-1 rounded text-emerald-400">config.json</code> sur le serveur
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center font-bold">5</span>
              <div>
                <p className="font-medium text-white">Executer et installer comme service</p>
                <p className="text-xs text-slate-500">
                  Double-cliquez sur <code className="bg-slate-800 px-1 rounded text-emerald-400">MySentinelAgent.exe</code> ou lancez{" "}
                  <code className="bg-slate-800 px-1 rounded text-emerald-400">install_service.bat</code> en administrateur
                </p>
              </div>
            </li>
          </ol>
        </div>
      ),
    },
    {
      id: "commands",
      title: "Options de ligne de commande",
      icon: Terminal,
      content: (
        <div className="space-y-2 text-sm">
          <div className="bg-slate-950 rounded-lg p-3 font-mono text-xs space-y-2">
            <div className="flex items-center gap-3">
              <code className="text-emerald-400">MySentinelAgent.exe</code>
              <span className="text-slate-500">Mode normal (boucle continue)</span>
            </div>
            <div className="flex items-center gap-3">
              <code className="text-emerald-400">MySentinelAgent.exe --run-once</code>
              <span className="text-slate-500">Execute une seule verification</span>
            </div>
            <div className="flex items-center gap-3">
              <code className="text-emerald-400">MySentinelAgent.exe --install-service</code>
              <span className="text-slate-500">Installe comme tache planifiee Windows</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "config",
      title: "Fichier config.json",
      icon: FileCode,
      content: (
        <div className="space-y-2 text-sm text-slate-300">
          <p>Le fichier <code className="bg-slate-800 px-1 rounded text-emerald-400">config.json</code> doit etre place a cote de l'executable :</p>
          <div className="bg-slate-950 rounded-lg p-3 font-mono text-xs text-slate-300">
            <pre>{`{
  "server_url": "https://votre-url-mysentinel.com",
  "server_id": "xxxxxxxx-xxxx-xxxx-xxxx",
  "api_key": "xxxxxxxx-xxxx-xxxx-xxxx",
  "agent_type": "veeam",         // ou "iperius"
  "frequency_minutes": 15        // intervalle en minutes
}`}</pre>
          </div>
          <div className="mt-2 space-y-1 text-xs text-slate-500">
            <p><span className="text-slate-300 font-medium">server_url</span> : URL de votre serveur ServeurOnline (HTTPS)</p>
            <p><span className="text-slate-300 font-medium">server_id</span> : Identifiant unique du serveur (copie depuis l'interface)</p>
            <p><span className="text-slate-300 font-medium">api_key</span> : Cle API secrete (copie depuis l'interface)</p>
            <p><span className="text-slate-300 font-medium">agent_type</span> : Type de logiciel de sauvegarde ("veeam" ou "iperius")</p>
            <p><span className="text-slate-300 font-medium">frequency_minutes</span> : Frequence de verification (defaut: 15 minutes)</p>
          </div>
        </div>
      ),
    },
    {
      id: "logs",
      title: "Logs et depannage",
      icon: FileText,
      content: (
        <div className="space-y-2 text-sm text-slate-300">
          <p>Les logs de l'agent sont enregistres dans :</p>
          <code className="block bg-slate-950 rounded-lg p-3 text-xs text-emerald-400 font-mono">C:\ProgramData\MySentinel\agent.log</code>
          <p className="font-medium text-white mt-3">Problemes courants :</p>
          <ul className="space-y-2 text-xs text-slate-400">
            <li className="flex gap-2"><span className="text-red-400 shrink-0">&#x2022;</span> <span><strong className="text-slate-300">Erreur de connexion</strong> : Verifiez que l'URL du serveur est accessible et que le pare-feu autorise HTTPS</span></li>
            <li className="flex gap-2"><span className="text-red-400 shrink-0">&#x2022;</span> <span><strong className="text-slate-300">Cmdlets Veeam non trouves</strong> : L'agent utilise automatiquement les logs fichiers comme fallback</span></li>
            <li className="flex gap-2"><span className="text-red-400 shrink-0">&#x2022;</span> <span><strong className="text-slate-300">Statut "unknown"</strong> : Aucune information trouvee - verifiez que le logiciel de sauvegarde est installe</span></li>
            <li className="flex gap-2"><span className="text-red-400 shrink-0">&#x2022;</span> <span><strong className="text-slate-300">API Key invalide</strong> : Regenerez la cle depuis la page du serveur dans ServeurOnline</span></li>
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="agents-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-white">Agents de monitoring</h1>
        <p className="text-slate-400 mt-1">Telechargez, configurez et deployez les agents sur vos serveurs</p>
      </div>

      {/* Agent selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["veeam", "iperius"].map((type) => (
          <div
            key={type}
            onClick={() => setSelectedAgent(type)}
            className={`glass-card rounded-lg p-5 cursor-pointer transition-all duration-200 border-2 ${
              selectedAgent === type ? "border-indigo-500" : "border-transparent hover:border-slate-600"
            }`}
            data-testid={`select-${type}`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${type === "veeam" ? "bg-green-500/20" : "bg-orange-500/20"}`}>
                {type === "veeam" ? <Server className="text-green-400" size={24} /> : <HardDrive className="text-orange-400" size={24} />}
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-white text-lg">{agentInfo[type].name}</h3>
                <p className="text-sm text-slate-400 mt-1">{agentInfo[type].description}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {agentInfo[type].features.map((f, i) => (
                    <span key={i} className={`text-xs px-2 py-1 rounded ${type === "veeam" ? "bg-green-500/10 text-green-400" : "bg-orange-500/10 text-orange-400"}`}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
              {selectedAgent === type && <CheckCircle className="text-indigo-500" size={24} />}
            </div>
          </div>
        ))}
      </div>

      {/* Configuration & Download */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Config form */}
        <div className="glass-card rounded-lg p-6">
          <h3 className="font-heading font-bold text-white mb-4 flex items-center gap-2">
            <Settings size={20} className="text-indigo-400" />
            Configuration de l'agent
          </h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">URL du serveur</Label>
              <Input
                value={configForm.serverUrl}
                onChange={(e) => setConfigForm({ ...configForm, serverUrl: e.target.value })}
                className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
                data-testid="server-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Server ID</Label>
              <Input
                value={configForm.serverId}
                onChange={(e) => setConfigForm({ ...configForm, serverId: e.target.value })}
                placeholder="Copiez depuis la page du serveur"
                className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
                data-testid="server-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">API Key</Label>
              <Input
                value={configForm.apiKey}
                onChange={(e) => setConfigForm({ ...configForm, apiKey: e.target.value })}
                placeholder="Copiez depuis la page du serveur"
                className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
                data-testid="api-key-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Frequence (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={configForm.frequencyMinutes}
                onChange={(e) => setConfigForm({ ...configForm, frequencyMinutes: parseInt(e.target.value) || 15 })}
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="frequency-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button onClick={downloadExePackage} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-exe-btn">
                <FolderArchive className="mr-2 h-4 w-4" />
                Package .exe
              </Button>
              <Button onClick={downloadScript} variant="secondary" className="bg-slate-800 hover:bg-slate-700" data-testid="download-script-btn">
                <Download className="mr-2 h-4 w-4" />
                Script PowerShell
              </Button>
            </div>
          </div>
        </div>

        {/* Quick start */}
        <div className="glass-card rounded-lg p-6">
          <h3 className="font-heading font-bold text-white mb-4 flex items-center gap-2">
            <Terminal size={20} className="text-emerald-400" />
            Demarrage rapide
          </h3>
          <ol className="space-y-4 text-slate-300">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center font-bold">1</span>
              <div>
                <p className="font-medium text-white">Creer un serveur dans l'interface</p>
                <p className="text-xs text-slate-500">Clients &gt; Votre client &gt; Ajouter un serveur</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center font-bold">2</span>
              <div>
                <p className="font-medium text-white">Copier le Server ID et l'API Key</p>
                <p className="text-xs text-slate-500">Depuis la page detail du serveur, cliquez sur les icones de copie</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center font-bold">3</span>
              <div>
                <p className="font-medium text-white">Telecharger le package .exe</p>
                <p className="text-xs text-slate-500">Remplissez le formulaire et cliquez sur "Package .exe"</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center font-bold">4</span>
              <div>
                <p className="font-medium text-white">Compiler et deployer</p>
                <p className="text-xs text-slate-500">
                  Lancez <code className="bg-slate-800 px-1 rounded text-emerald-400">build.bat</code>, puis copiez l'.exe et config.json sur le serveur
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center font-bold">5</span>
              <div>
                <p className="font-medium text-white">Installer comme service (optionnel)</p>
                <p className="text-xs text-slate-500">
                  <code className="bg-slate-800 px-1 rounded text-emerald-400">MySentinelAgent.exe --install-service</code> en administrateur
                </p>
              </div>
            </li>
          </ol>
        </div>
      </div>

      {/* Code preview */}
      <div className="glass-card rounded-lg overflow-hidden">
        <Tabs defaultValue="config">
          <div className="border-b border-slate-700 px-4">
            <TabsList className="bg-transparent">
              <TabsTrigger value="config" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                <FileCode size={16} className="mr-2" />
                config.json
              </TabsTrigger>
              <TabsTrigger value="scheduled" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
                <Clock size={16} className="mr-2" />
                Tache planifiee
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="config" className="m-0">
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generateConfigJson())} className="absolute top-2 right-2 text-slate-400 hover:text-white" data-testid="copy-config-btn">
                <Copy size={16} className="mr-1" /> Copier
              </Button>
              <pre className="p-4 text-sm font-mono text-slate-300">{generateConfigJson()}</pre>
            </div>
          </TabsContent>

          <TabsContent value="scheduled" className="m-0">
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  copyToClipboard(`schtasks /create /tn "MySentinelAgent" /tr "\\"C:\\MySentinel\\MySentinelAgent.exe\\"" /sc onstart /ru SYSTEM /rl HIGHEST /f`)
                }
                className="absolute top-2 right-2 text-slate-400 hover:text-white"
              >
                <Copy size={16} className="mr-1" /> Copier
              </Button>
              <pre className="p-4 text-xs font-mono text-emerald-400 overflow-x-auto">
{`# Methode 1: Via l executable (recommande)
MySentinelAgent.exe --install-service

# Methode 2: Via schtasks (manuel)
schtasks /create /tn "MySentinelAgent" ^
  /tr "\\"C:\\MySentinel\\MySentinelAgent.exe\\"" ^
  /sc onstart /ru SYSTEM /rl HIGHEST /f

# Methode 3: Via PowerShell
$action = New-ScheduledTaskAction -Execute "C:\\MySentinel\\MySentinelAgent.exe"
$trigger = New-ScheduledTaskTrigger -AtStartup
$principal = New-ScheduledTaskPrincipal -UserId "SYSTEM" -RunLevel Highest
Register-ScheduledTask -TaskName "MySentinelAgent" -Action $action -Trigger $trigger -Principal $principal`}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Documentation */}
      <div className="glass-card rounded-lg overflow-hidden" data-testid="agent-documentation">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="font-heading font-bold text-white flex items-center gap-2">
            <BookOpen size={20} className="text-indigo-400" />
            Documentation complete
          </h3>
        </div>
        <div className="divide-y divide-slate-800">
          {docSections.map((section) => {
            const SIcon = section.icon;
            const isOpen = expandedDoc === section.id;
            return (
              <div key={section.id}>
                <button
                  onClick={() => setExpandedDoc(isOpen ? null : section.id)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-800/30 transition-colors text-left"
                  data-testid={`doc-${section.id}`}
                >
                  <span className="flex items-center gap-3">
                    <SIcon size={16} className="text-slate-500" />
                    <span className="text-sm font-medium text-white">{section.title}</span>
                  </span>
                  {isOpen ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                </button>
                {isOpen && <div className="px-6 pb-4">{section.content}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ZIP contents info */}
      <div className="glass-card rounded-lg p-6">
        <h3 className="font-heading font-bold text-white mb-4 flex items-center gap-2">
          <FolderArchive size={20} className="text-amber-400" />
          Contenu du package .exe
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { name: "mysentinel_agent.py", desc: "Code source Python de l'agent", icon: "py", color: "text-blue-400" },
            { name: "config.json", desc: "Configuration pre-remplie avec vos identifiants", icon: "json", color: "text-emerald-400" },
            { name: "build.bat", desc: "Script de compilation en .exe (PyInstaller)", icon: "bat", color: "text-amber-400" },
            { name: "install_service.bat", desc: "Script d'installation comme service Windows", icon: "bat", color: "text-amber-400" },
            { name: "README.txt", desc: "Guide complet de deploiement", icon: "txt", color: "text-slate-400" },
          ].map((file) => (
            <div key={file.name} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
              <p className={`font-mono text-xs ${file.color} font-medium`}>{file.name}</p>
              <p className="text-[11px] text-slate-500 mt-1">{file.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Agents;
